import { ShowSection } from 'framework/ui/layout';
import Milestones from './Milestones';
import { FormShowTemplate } from 'framework/form';
import {
  BillingType,
  Milestone,
  MILESTONE_STATUS,
  OUTCOME_STATUS,
  useProjectDataContext,
} from 'domain/shared/newProject';
import { MilestonesType } from './types';
import useMilestonesService, { MILESTONES_ALIAS } from './useMilestonesService';
import { ProjectOutcomesResponse } from '../businessOutcomes/hooks/useOutcomes';
import { omit } from 'lodash';
import { BusinessOutcomesType } from '../businessOutcomes/types';
import { EditableListFormShowTemplate, NEW_ROW_ID_PREFIX } from 'framework/list';

const withDisplayFlags = (milestone: MilestonesType) => ({
  ...milestone,
  hasInitialPlannedDateFromBackend: !!milestone.initialPlannedDate,
});

const MilestonesContext = ({ data }: { data?: ProjectOutcomesResponse }) => {
  const { milestonesBatch, getMilestonesValidationSchema } = useMilestonesService();
  const { data: projectData } = useProjectDataContext();

  const isMilestoneBillingType = data?.budget?.billingType === BillingType.MILESTONE;
  const businessOutcomes = data?.businessOutcomes?.map(
    ({ alias = '', name = '', status = OUTCOME_STATUS.ACTIVE }: BusinessOutcomesType) => ({
      alias,
      name,
      status,
    }),
  );

  const isRejectionReason = (milestone: Milestone) => milestone?.status === MILESTONE_STATUS.REJECTED;

  const isChangeReason = data?.milestones?.some((milestone: MilestonesType) => milestone?.comment);

  const processedData = {
    ...data,
    milestones: data?.milestones?.map(withDisplayFlags),
  };

  return (
    <ShowSection sx={{ '&.section-details__wrapper': { p: '0px !important' } }}>
      <FormShowTemplate
        isInitialsLoadingPosition
        initialData={processedData}
        submitFormFunc={async values => {
          const itemsToSubmit = values.milestones
            .filter((item: Record<string, unknown>) => !item['_isMarkedForDeletion'])
            .map((v: MilestonesType) =>
              omit(
                {
                  ...v!,
                  ...(v.id.toString().startsWith(NEW_ROW_ID_PREFIX) && {
                    id: null,
                    alias: null,
                    status: OUTCOME_STATUS.ACTIVE,
                  }),
                  type:
                    data?.budget?.billingType === BillingType.PERIODIC_PREPAID ||
                    data?.budget?.billingType === BillingType.PERIODIC_POSTPAID
                      ? MILESTONES_ALIAS.NON_BILLING_MILESTONE
                      : v.type || MILESTONES_ALIAS.NON_BILLING_MILESTONE,
                },
                ['__typename', 'hasInitialPlannedDateFromBackend'],
              ),
            );

          const savedMilestones = (await milestonesBatch(projectData.id, itemsToSubmit)) as MilestonesType[];
          return { milestones: savedMilestones.map(withDisplayFlags) };
        }}
        afterSubmit={{
          message: 'You are awesome! Project milestones section has been successfully updated.',
        }}
        getValidationSchemaFunc={getMilestonesValidationSchema}
      >
        <EditableListFormShowTemplate listSource="milestones" localStorageKey={`project:${data?.id}:milestones`}>
          <Milestones
            isMilestoneBillingType={isMilestoneBillingType}
            businessOutcomes={businessOutcomes}
            isRejectionReason={isRejectionReason}
            isChangeReason={isChangeReason}
          />
        </EditableListFormShowTemplate>
      </FormShowTemplate>
    </ShowSection>
  );
};

export default MilestonesContext;
